import { jsx as _jsx } from "react/jsx-runtime";
/**
 * @fileoverview TasteSlider — LOCI slider atom for RefineTasteCard (FRE-774).
 *
 * Native `<input type="range">` for free keyboard a11y: Arrow keys adjust by
 * step, Home/End jump to min/max, and the DOM element auto-exposes
 * `aria-valuemin` / `aria-valuemax` / `aria-valuenow`. The track is teal
 * (Loci skyline) and the thumb is amber (electric amber) per the design
 * tokens; both hover and focus states pulse the amber ring.
 */
import { forwardRef, useId, useInsertionEffect } from 'react';
import { useTheme } from '../../../theme';
const TEAL_TRACK = '#4FC4C4';
const TEAL_TRACK_BG = 'rgba(79, 196, 196, 0.24)';
const STYLE_ELEMENT_ID = 'loci-taste-slider-styles';
const TASTE_SLIDER_STYLES = `
input[data-taste-slider] {
  -webkit-appearance: none;
  appearance: none;
  width: 100%;
  height: 24px;
  background: transparent;
  cursor: pointer;
  outline: none;
  padding: 0;
  margin: 0;
}
input[data-taste-slider]::-webkit-slider-runnable-track {
  height: 6px;
  border-radius: 9999px;
  background: var(--taste-slider-track);
}
input[data-taste-slider]::-moz-range-track {
  height: 6px;
  border-radius: 9999px;
  background: var(--taste-slider-track);
}
input[data-taste-slider]::-webkit-slider-thumb {
  -webkit-appearance: none;
  appearance: none;
  width: 18px;
  height: 18px;
  margin-top: -6px;
  border-radius: 9999px;
  background: var(--taste-slider-thumb);
  border: 2px solid var(--taste-slider-thumb-fg);
  box-shadow: 0 0 0 0 var(--taste-slider-thumb-ring);
  transition: box-shadow 150ms ease-out, transform 150ms ease-out;
}
input[data-taste-slider]::-moz-range-thumb {
  width: 18px;
  height: 18px;
  border-radius: 9999px;
  background: var(--taste-slider-thumb);
  border: 2px solid var(--taste-slider-thumb-fg);
  box-shadow: 0 0 0 0 var(--taste-slider-thumb-ring);
  transition: box-shadow 150ms ease-out, transform 150ms ease-out;
}
input[data-taste-slider]:hover::-webkit-slider-thumb,
input[data-taste-slider]:focus-visible::-webkit-slider-thumb {
  box-shadow: 0 0 0 6px var(--taste-slider-thumb-ring);
  transform: scale(1.05);
}
input[data-taste-slider]:hover::-moz-range-thumb,
input[data-taste-slider]:focus-visible::-moz-range-thumb {
  box-shadow: 0 0 0 6px var(--taste-slider-thumb-ring);
  transform: scale(1.05);
}
@media (prefers-reduced-motion: reduce) {
  input[data-taste-slider]::-webkit-slider-thumb,
  input[data-taste-slider]::-moz-range-thumb {
    transition: none;
  }
}
`;
/**
 * Inject the shared slider stylesheet into the document exactly once per
 * page, regardless of how many `TasteSlider` instances mount. Uses an
 * `id`-scoped `<style>` in `document.head` so React reconciliation and
 * Storybook hot-reloads don't produce duplicate sheets.
 */
function useSharedTasteSliderStyles() {
    useInsertionEffect(() => {
        if (typeof document === 'undefined')
            return;
        if (document.getElementById(STYLE_ELEMENT_ID))
            return;
        const el = document.createElement('style');
        el.id = STYLE_ELEMENT_ID;
        el.textContent = TASTE_SLIDER_STYLES;
        document.head.appendChild(el);
    }, []);
}
/**
 * Controlled range slider with amber thumb / teal track. Visible track fill
 * is a CSS gradient driven by the current value, applied via CSS custom
 * properties so the static stylesheet above stays value-agnostic.
 */
export const TasteSlider = forwardRef(function TasteSlider({ ariaLabel, labelledBy, min, max, step, value, onValueChange, className, style, ...rest }, ref) {
    useSharedTasteSliderStyles();
    const theme = useTheme();
    const reactId = useId();
    const inputId = `taste-slider-${reactId}`;
    const safeMax = max > min ? max : min + 1;
    const pct = Math.max(0, Math.min(100, ((value - min) / (safeMax - min)) * 100));
    const amberThumb = theme.colors.accent;
    const amberThumbFg = theme.colors.accentForeground;
    const trackGradient = `linear-gradient(to right, ${TEAL_TRACK} 0%, ${TEAL_TRACK} ${pct}%, ${TEAL_TRACK_BG} ${pct}%, ${TEAL_TRACK_BG} 100%)`;
    const internalStyle = {
        ['--taste-slider-track']: trackGradient,
        ['--taste-slider-thumb']: amberThumb,
        ['--taste-slider-thumb-ring']: `${amberThumb}66`,
        ['--taste-slider-thumb-fg']: amberThumbFg,
    };
    const mergedStyle = { ...internalStyle, ...style };
    return (_jsx("input", { ref: ref, id: inputId, type: "range", "data-taste-slider": "", "aria-label": labelledBy ? undefined : ariaLabel, "aria-labelledby": labelledBy, min: min, max: max, step: step, value: value, onChange: (e) => {
            const next = Number(e.target.value);
            if (Number.isFinite(next))
                onValueChange(next);
        }, className: className, style: mergedStyle, ...rest }));
});
